import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
} from "@mui/material";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

/**
 * Reusable UpgradePlanDialog component for showing upgrade prompts when feature limits are reached
 * 
 * @param {boolean} open - Controls dialog visibility
 * @param {function} onClose - Callback when dialog is closed
 * @param {object} selectedBusiness - Business object containing plan information
 * @param {string} featureType - Type of feature ("locations", "menus", "categories", "items")
 * @param {number} currentQuantity - Current number of items the user has
 * @param {number} maxQuantity - Maximum allowed quantity for current plan
 * @param {function} onUpgrade - Callback when upgrade button is clicked
 * 
 * @example
 * // For locations
 * <UpgradePlanDialog
 *   open={showUpgradeDialog}
 *   onClose={() => setShowUpgradeDialog(false)}
 *   selectedBusiness={selectedBusiness}
 *   featureType="locations"
 *   currentQuantity={currentLocations}
 *   maxQuantity={maxLocations}
 *   onUpgrade={() => navigate('/subscription/upgrade')}
 * />
 * 
 * @example
 * // For menus
 * <UpgradePlanDialog
 *   open={showUpgradeDialog}
 *   onClose={() => setShowUpgradeDialog(false)}
 *   selectedBusiness={selectedBusiness}
 *   featureType="menus"
 *   currentQuantity={menus.length}
 *   maxQuantity={menuFeature.quantity}
 *   onUpgrade={() => handleUpgrade()}
 * />
 */
const UpgradePlanDialog = ({
  open,
  onClose,
  selectedBusiness,
  featureType = "locations", // Default to locations for backward compatibility
  currentQuantity = 0,
  maxQuantity = 0,
  onUpgrade,
}) => {
  const { t } = useTranslation("dashboard");

  // Feature configuration mapping
  const featureConfig = {
    locations: {
      title: t("upgradePlanLocationsTitle"),
      subtitle: t("upgradePlanLocationsSubtitle"),
      description: t("upgradePlanLocationsDesc"),
      limitText: t("upgradePlanLocationsLimitText"),
      countText: t("upgradePlanLocationsCountText", { count: maxQuantity }),
      moreText: t("upgradePlanLocationsMore"),
      featureKey: "locations"
    },
    menus: {
      title: t("upgradePlanMenusTitle"),
      subtitle: t("upgradePlanMenusSubtitle"),
      description: t("upgradePlanMenusDesc"),
      limitText: t("upgradePlanMenusLimitText"),
      countText: t("upgradePlanMenusCountText", { count: maxQuantity }),
      moreText: t("upgradePlanMenusMore"),
      featureKey: "menu_catalogs"
    },
    categories: {
      title: t("upgradePlanCategoriesTitle"),
      subtitle: t("upgradePlanCategoriesSubtitle"),
      description: t("upgradePlanCategoriesDesc"),
      limitText: t("upgradePlanCategoriesLimitText"),
      countText: t("upgradePlanCategoriesCountText", { count: maxQuantity }),
      moreText: t("upgradePlanCategoriesMore"),
      featureKey: "categories"
    },
    items: {
      title: t("upgradePlanItemsTitle"),
      subtitle: t("upgradePlanItemsSubtitle"),
      description: t("upgradePlanItemsDesc"),
      limitText: t("upgradePlanItemsLimitText"),
      countText: t("upgradePlanItemsCountText", { count: maxQuantity }),
      moreText: t("upgradePlanItemsMore"),
      featureKey: "items_per_menu"
    },
    team_members: {
      title: t("upgradePlanTeamTitle"),
      subtitle: t("upgradePlanTeamSubtitle"),
      description: t("upgradePlanTeamDesc"),
      limitText: t("upgradePlanTeamLimitText"),
      countText: t("upgradePlanTeamCountText", { count: maxQuantity }),
      moreText: t("upgradePlanTeamMore"),
      featureKey: "team_members"
    }
  };

  const config = featureConfig[featureType] || featureConfig.locations;

  const handleUpgrade = () => {
    onClose();
    if (onUpgrade) {
      onUpgrade();
    } else {
      // Default behavior - show toast and redirect
      toast.success("Redirecting to upgrade page...");
      // TODO: Add actual navigation to upgrade page
      // Example: navigate('/subscription/upgrade');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0 20px 40px rgba(46, 45, 45, 0.74)",
          border: "1px solid rgba(125, 60, 255, 0.1)",
          overflow: "hidden"
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          textAlign: "center", 
          pb: 2,
          pt: 4,
        //   background: "linear-gradient(135deg, #7d3cff 0%, #6c00e6 100%)",
          color: "white",
          position: "relative"
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {config.title}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 400 }}>
          {config.subtitle}
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ textAlign: "center", py: 4, px: 4 }}>
        {/* Plan Info Card */}
        <Box
          sx={{
            bgcolor: "#f8f9ff",
            borderRadius: "12px",
            p: 3,
            mb: 3,
            border: "1px solid #e3e8ff"
          }}
        >
          <Typography variant="body1" sx={{ mb: 2, color: "#4a5568", fontWeight: 500 }}>
            {t("upgradePlanCurrentPlanDetails")}
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="body2" sx={{ color: "#718096" }}>
              {t("upgradePlanPlanName")}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600, 
                color: "#7d3cff",
                bgcolor: "#e3e8ff",
                px: 2,
                py: 0.5,
                borderRadius: "6px"
              }}
            >
              {selectedBusiness?.planName || "Basic"}
            </Typography>
          </Box>
          
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="body2" sx={{ color: "#718096" }}>
              {config.limitText}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#2d3748" }}>
              {config.countText}
            </Typography>
          </Box>
        </Box>

        <Typography variant="body1" sx={{ color: "#4a5568", lineHeight: 1.6, mb: 1 }}>
          {config.description}
        </Typography>
        
        <Typography variant="body2" sx={{ color: "#718096", lineHeight: 1.5 }}>
          {config.moreText}
        </Typography>
      </DialogContent>
      
      <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 4, px: 4 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            px: 4,
            py: 1.5,
            textTransform: "none",
            borderColor: "#e2e8f0",
            color: "#fff",
            borderRadius: "8px",
            fontWeight: 500,
            minWidth: "120px",
            "&:hover": {
              borderColor: "#cbd5e0",
              backgroundColor: "#f7fafc",
              color: "#4a5568"
            }
          }}
        >
          {t("upgradePlanCancel")}
        </Button>
        <Button
          onClick={handleUpgrade}
          variant="contained"
          sx={{
            px: 4,
            py: 1.5,
            textTransform: "none",
            background: "linear-gradient(135deg, #7d3cff 0%, #6c00e6 100%)",
            borderRadius: "8px",
            fontWeight: 600,
            minWidth: "140px",
            boxShadow: "0 4px 12px rgba(125, 60, 255, 0.3)",
            "&:hover": {
              background: "linear-gradient(135deg, #6c00e6 0%, #5a00c2 100%)",
              boxShadow: "0 6px 16px rgba(125, 60, 255, 0.4)"
            }
          }}
        >
          {t("upgradePlanUpgrade")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpgradePlanDialog;
